/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button, Tooltip, Input, Form } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadBookingVsDeliveryReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { BOOKING_TYPE_OPTIONS } from "../../../../constants/reportConstants";

const BookingVsDeliveryReport = () => {
    const { createNewRequest, isLatestRequest, cancelPreviousRequest } = useRequestCancellation();

    const [headerHubList, setHeaderHubList] = useState([]);

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Booking vs Delivery Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedCps, setSelectedCps] = useState([]);
    const [bookingTypes, setBookingTypes] = useState([]);
    const [selectedBookingTypes, setSelectedBookingTypes] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");
    const [awbInput, setAwbInput] = useState("");
    const [awbFilter, setAwbFilter] = useState("");
    const awbInputRef = useRef(null);
    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([newActiveHubId]);
            } else {
                setSelectedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Combined initial setup useEffect
    useEffect(() => {
        const initializeData = async () => {
            try {
                // Get user info first — always enforced for hub-restricted roles
                const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userRole = user?.roles?.[0];
                setHubUser(userRole);

                // Fetch hub list
                const hubs = await getHubList();
                setHubList(hubs);

                const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
                const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

                if (!hubInitializedRef.current) {
                    const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
                    const userHubs = userFullData?.userInfo?.hubDetails || [];

                    if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                        setSelectedHubs([currentActiveHubId]);
                    } else if (userHubs?.length === 0 || userRole === "ROLE_HOADMIN" || userRole === "ROLE_CENTRALOPERATOR") {
                        setSelectedHubs([]);
                    } else {
                        setSelectedHubs(["ALL"]);
                    }
                    hubInitializedRef.current = true;
                }
            } catch (error) {
                message.error("Failed to fetch initial data");
            }
        };

        initializeData();
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    // Fetch CP list when hubs change
    useEffect(() => {
        if (selectedHubs.length > 0) {
            Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)))
                .then(cpLists => {
                    const allCps = [...new Set(cpLists.flat())];
                    setCpList(allCps);
                })
                .catch(() => {
                    message.error("Failed to fetch CP lists");
                    setCpList([]);
                });
        } else {
            setCpList([]);
        }
    }, [selectedHubs]);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }
        // For HUBADMIN users, hub selection is mandatory
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
            return; // Don"t fetch if HUBADMIN has no hub selected
        }

        // Debounce the API call to prevent multiple rapid calls
        const timer = setTimeout(() => {
            fetchReport();
        }, 300);

        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [hubUser, selectedHubs, selectedCps, selectedBookingTypes, dateRange, awbFilter, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        setLoading(true);
        setReportData([]);

        // Create new request with cancellation support
        const { signal, requestId } = createNewRequest("booking-vs-delivery-report");

        try {
            // For HUBADMIN users, hub selection is mandatory
            if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
                return;
            }

            // Resolve From Hubs (selectedHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedHubIds = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedHubData = activeHubOptions.filter(hub => resolvedHubIds?.includes(hub.value));
            const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));
            const selectedBookingTypeData = BOOKING_TYPE_OPTIONS.filter(bt => selectedBookingTypes?.includes(bt.value));

            const params = {
                page: pagination.current - 1, // Convert to 0-based for API
                size: pagination.pageSize,
                ...(dateRange[0] && { fromDate: dateRange[0].toISOString().split("T")[0] }),
                ...(dateRange[1] && { toDate: dateRange[1].toISOString().split("T")[0] }),
                ...(selectedBookingTypeData?.length > 0 && { bookingType: selectedBookingTypeData.map(bt => bt.value).join(",") }),
                ...(resolvedHubIds?.length > 0 && { fromHubId: resolvedHubIds.join(",") }),
                ...(selectedCpData?.length > 0 && { bookingCpId: selectedCpData.map(cp => cp.value).join(",") }),
                ...(awbFilter && { awbNumbers: awbFilter.split(",").map(awb => awb.trim()).filter(Boolean) })
            };

            const response = await ReportsService.getBookingVsDeliveryReport(params, { signal });

            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring response");
                return;
            }

            if (response?.lastRefreshedAt) {
                setLastsynced(response.lastRefreshedAt);
            }
            if (response?.nextScheduledRefresh) {
                setNextScheduledRefresh(response.nextScheduledRefresh);
            }
            if (response?.data) {
                setReportData(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0,
                }));
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
                message.error("No data available");
            }
        } catch (error) {
            // Check if this is still the latest request
            if (!isLatestRequest(requestId)) {
                console.log("🔥 Request cancelled - ignoring error");
                return;
            }

            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled");
                return;
            }

            message.error(error?.message || "Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            // Only update loading state if this is still the latest request
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 60,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Document No.",
            dataIndex: "documentno",
            key: "documentno",
            sorter: true,
            width: 150,
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        {
            title: "Booking Date",
            dataIndex: "bookingDatetime",
            key: "bookingDatetime",
            sorter: true,
            width: 150,
            render: (value) => value ? moment(value).format("DD-MM-YYYY HH:mm") : "-"
        },
        {
            title: "Booking Type",
            dataIndex: "bookingType",
            key: "bookingType",
            sorter: true,
            width: 120
        },
        {
            title: "Document Type",
            dataIndex: "documentType",
            key: "documentType",
            sorter: true,
            width: 120
        },
        {
            title: "From Hub",
            dataIndex: "fromHub",
            key: "fromHub",
            sorter: true,
            width: 180
        },
        {
            title: "Booking CP",
            dataIndex: "bookingCp",
            key: "bookingCp",
            sorter: true,
            width: 150
        },
        {
            title: "State",
            dataIndex: "state",
            key: "state",
            sorter: true,
            width: 120
        },
        {
            title: "From Center",
            dataIndex: "fromCenter",
            key: "fromCenter",
            sorter: true,
            width: 120
        },
        {
            title: "To Center",
            dataIndex: "toCenter",
            key: "toCenter",
            sorter: true,
            width: 120
        },
        {
            title: "Sender",
            dataIndex: "sender",
            key: "sender",
            sorter: true,
            width: 150
        },
        {
            title: "Receiver",
            dataIndex: "receiver",
            key: "receiver",
            sorter: true,
            width: 150
        },
        {
            title: "Delivery Date",
            dataIndex: "deliveryDate",
            key: "deliveryDate",
            sorter: true,
            width: 120,
            render: (value) => value ? moment(value).format("DD-MM-YYYY") : "-"
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            sorter: true,
            width: 100
        },
        {
            title: "Charges",
            dataIndex: "charges",
            key: "charges",
            sorter: true,
            width: 100,
            align: "right",
            render: (value) => value ? `₹${value}` : "-"
        },

    ];

    // Handler functions for filters
    const handleDateChange = (dates) => {
        setDateRange(dates);
    };

    const handleHubChange = (value) => {
        setSelectedHubs(value);
        if (value?.length > 0) {
            // Fetch CP list when hubs change
            Promise.all(value.map(hubId => getHubCPList(hubId)))
                .then(cpLists => {
                    const allCps = [...new Set(cpLists.flat())];
                    setCpList(allCps);
                })
                .catch(() => {
                    message.error("Failed to fetch CP lists");
                    setCpList([]);
                });
        } else {
            setCpList([]);
        }
    };

    const handleCpChange = (value) => {
        setSelectedCps(value);
    };

    const handleBookingTypeChange = (value) => {
        setSelectedBookingTypes(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleDownload = async () => {
        if (!dateRange[0] || !dateRange[1]) {
            message.error("Please select a date range for download");
            return;
        }

        /* if (selectedHubs.length === 0) {
             message.error("Please select at least one hub for download");
             return;
         }*/

        setDownloadLoading(true);
        try {
            // Resolve From Hubs (selectedHubs) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedHubIds = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            const selectedHubData = activeHubOptions.filter(hub => resolvedHubIds?.includes(hub.value));
            const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));
            const selectedBookingTypeData = BOOKING_TYPE_OPTIONS.filter(bt => selectedBookingTypes?.includes(bt.value));

            const params = {
                fromDate: dateRange[0].toISOString().split("T")[0],
                toDate: dateRange[1].toISOString().split("T")[0],
                ...(selectedBookingTypeData?.length > 0 && { bookingTypes: selectedBookingTypeData.map(bt => bt.value).join(",") }),
                ...(resolvedHubIds?.length > 0 && { fromHubIds: resolvedHubIds.join(",") }),
                ...(selectedCpData?.length > 0 && { bookingCpIds: selectedCpData.map(cp => cp.value).join(",") }),
                ...(awbFilter && { awbNumbers: awbFilter.split(",").map(awb => awb.trim()).filter(Boolean) })
            };

            await downloadBookingVsDeliveryReport(params);
            message.success("Booking vs Delivery Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error(error?.message || "Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading
                    title="Booking vs Delivery Report"
                    newReportUrl="/reports/booking-vs-delivery-report/"
                >
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateChange}
                            format="YYYY-MM-DD"
                            style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
                            allowClear
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            placeholder="Select From Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedHubs, baseOptions);
                                setSelectedCps([]); // Clear CP selection when hubs change
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <div className="filter-label">Booking CP</div>
                        <DropDownComponent
                            placeholder="Booking CP"
                            options={cpList}
                            onChange={handleCpChange}
                            value={selectedCps}
                            allowClear
                            style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <div className="filter-label">Booking Type</div>
                        <DropDownComponent
                            placeholder="Select Booking Type"
                            options={BOOKING_TYPE_OPTIONS.filter(opt => opt.value !== "ALL")}
                            onChange={handleBookingTypeChange}
                            value={selectedBookingTypes}
                            allowClear
                            style={{ height: "32px", borderRadius: "5px", backgroundColor: "#fff" }}
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={5}>
                        <div className="filter-label">AWB Number</div>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Input
                                ref={awbInputRef}
                                placeholder="Search AWB record..."
                                value={awbInput}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setAwbInput(value);
                                    if (!value) {
                                        setAwbFilter("");
                                    }
                                }}
                                onPressEnter={() => setAwbFilter(awbInput)}
                                onClear={() => {
                                    setAwbInput("");
                                    setAwbFilter("");
                                }}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData("text");
                                    // Split by newlines, tabs, or multiple spaces and filter empty strings
                                    const processedText = pastedText
                                        .split(/[\r\n\t ]+/)
                                        .map(item => item.trim())
                                        .filter(item => item !== "")
                                        .join(",");

                                    if (processedText) {
                                        // If there's already text, append with a comma if needed
                                        const newValue = awbInput
                                            ? `${awbInput.trim().replace(/,+$/, "")},${processedText}`
                                            : processedText;
                                        setAwbInput(newValue);

                                        // Scroll to end after state update
                                        setTimeout(() => {
                                            if (awbInputRef.current) {
                                                const inputElement = awbInputRef.current.input;
                                                if (inputElement) {
                                                    inputElement.scrollLeft = inputElement.scrollWidth;
                                                    // Also set cursor to end
                                                    const len = newValue.length;
                                                    inputElement.setSelectionRange(len, len);
                                                }
                                            }
                                        }, 0);
                                    }
                                }}
                                style={{
                                    height: "32px",
                                    maxHeight: "32px",
                                    borderRadius: "5px",
                                    border: "1px solid #d9d9d9",
                                    backgroundColor: "#fff",
                                    overflow: "hidden"
                                }}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={3} style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                        <div className="filter-label">&nbsp;</div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={!dateRange[0] || !dateRange[1]}
                            style={{ height: "32px", width: "100%", borderRadius: "5px" }}
                        >
                            Download
                        </Button>
                    </Col>
                </Row>
            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 350px)", x: "max-content" }}
                sticky
            />
        </div>
    );
};

export default BookingVsDeliveryReport;
